/**
 * CoqTile — Cost of Quality trailing 12 months tile + tiny bar showing the
 * category breakdown. Drafts/53 §A row + §B feed.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCostAggregates, type CostCategory } from '../../api/costHooks';

const CATEGORY_COLOURS: Record<CostCategory, string> = {
  INTERNAL_LABOUR:    'var(--tenant-primary, #0A84FF)',
  EXTERNAL_LAB:       '#0EA5E9',
  MATERIAL_SCRAP:     'var(--color-status-error, #B91C1C)',
  EXPEDITED_SHIPPING: 'var(--color-status-warning, #B45309)',
  REGULATORY_FEE:     '#7C3AED',
  OTHER:              'var(--color-text-tertiary, #6B7989)',
};

const CATEGORY_LABEL: Record<CostCategory, string> = {
  INTERNAL_LABOUR:    'Labour',
  EXTERNAL_LAB:       'Ext. lab',
  MATERIAL_SCRAP:     'Scrap',
  EXPEDITED_SHIPPING: 'Freight',
  REGULATORY_FEE:     'Reg. fee',
  OTHER:              'Other',
};

export function CoqTile() {
  const { t, i18n } = useTranslation();
  const q = useCostAggregates();

  const fmt = useMemo(() => {
    const code = q.data?.currencyCode ?? 'USD';
    return (cents: number) => {
      try {
        return new Intl.NumberFormat(i18n.language, {
          style: 'currency', currency: code, maximumFractionDigits: 0,
        }).format(cents / 100);
      } catch {
        return `${code} ${(cents / 100).toLocaleString(i18n.language, { maximumFractionDigits: 0 })}`;
      }
    };
  }, [i18n.language, q.data?.currencyCode]);

  const segments = useMemo(() => {
    if (!q.data) return [];
    const total = Math.max(1, q.data.trailing12mTotalCents);
    return q.data.byCategory.map((row) => ({
      key: `${row.sourceKind}-${row.costCategory}`,
      cat: row.costCategory,
      pct: (row.amountCentsTotal / total) * 100,
      amount: row.amountCentsTotal,
    }));
  }, [q.data]);

  return (
    <div style={{
      padding: 16, borderRadius: 14, border: '1px solid var(--color-border-subtle, #E5E7EB)',
      background: 'var(--color-bg-surface-elevated, #FFFFFF)', display: 'flex', flexDirection: 'column', gap: 12,
      minWidth: 0,
    }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span aria-hidden style={{ width: 8, height: 8, borderRadius: 9999, background: 'var(--tenant-primary, #0A84FF)' }} />
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #4B5563)', flex: 1 }}>
          {t('qualityDashboard.coq.title', { defaultValue: 'Cost of Quality · trailing 12 months' })}
        </span>
      </header>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 28, fontWeight: 600, color: 'var(--color-text-primary, #111827)', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
          {q.data ? fmt(q.data.trailing12mTotalCents) : '—'}
        </span>
        <span style={{ fontSize: 12, color: 'var(--color-text-tertiary, #6B7989)' }}>
          {q.data ? `${q.data.byCategory.length} categories` : ''}
        </span>
      </div>
      {/* Single horizontal stack-bar */}
      <div role="img" aria-label="Cost of Quality breakdown by category">
        <svg width="100%" height={10} viewBox="0 0 100 10" preserveAspectRatio="none">
          {segments.reduce<{ cursor: number; nodes: JSX.Element[] }>((acc, s) => {
            const w = s.pct;
            acc.nodes.push(
              <rect key={s.key} x={acc.cursor} y={0} width={w} height={10}
                fill={CATEGORY_COLOURS[s.cat]} />
            );
            acc.cursor += w;
            return acc;
          }, { cursor: 0, nodes: [] }).nodes}
        </svg>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {segments.slice(0, 4).map((s) => (
          <li key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-secondary, #4B5563)' }}>
            <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: CATEGORY_COLOURS[s.cat] }} />
            <span>{CATEGORY_LABEL[s.cat]}</span>
            <strong style={{ color: 'var(--color-text-primary, #111827)' }}>{fmt(s.amount)}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}
